import type { DeskDetail, DeskFile, DeskFileAuthor, DeskFileCommit, DeskFileTree, DeskOpenWorkItemFiles } from '@deskworks/shared-types';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Bot,
  ChevronRight,
  Copy,
  Download,
  ExternalLink,
  FilePlus,
  FileText,
  Folder,
  FolderOpen,
  FolderTree,
  GitBranch,
  GitMerge,
  Globe,
  History,
  Image as ImageIcon,
  Lock,
  Maximize2,
  Pencil,
  RotateCcw,
  Search,
  Trash2,
  Upload,
  User,
  X,
} from 'lucide-react';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Confirm } from '@/components/confirm';
import { Field } from '@/components/field';
import { TextDiff } from '@/components/files/text-diff';
import { Markdown } from '@/components/markdown';
import { DefinitionList } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { DropZone } from '@/components/resources/resources';
import { Section } from '@/components/section';
import { MetaBadge } from '@/components/status-badge';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, errorMessage } from '@/lib/api';
import {
  authorName,
  baseName,
  CHANGE_KIND,
  cleanPath,
  deskFilesApi,
  dirName,
  downloadDeskFile,
  fileKind,
  fileToBase64,
  isImageFile,
  isPdfFile,
  isTextFile,
  joinPath,
  MAX_TEXT_BYTES,
  MAX_UPLOAD_BYTES,
  newestChange,
  shortSha,
  textToBase64,
  useDeskBlob,
  useDeskBlobText,
  useObjectUrl,
  WORK_ITEM_FILES_STATUS,
} from '@/lib/desk-files';
import { formatBytes } from '@/lib/format';
import { useApiMutation, useIsPhone } from '@/lib/hooks';
import { t, tc, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

/** Up to this many files every folder starts open. */
const OPEN_ALL_UP_TO = 40;
const HISTORY_PAGE = 30;

/** Whether the person may change the desk's files (DeskFileTree.canWrite); without, every write action is hidden. */
const CanWrite = createContext(true);

const copyLink = (url: string, toast: ReturnType<typeof useToast>) =>
  void navigator.clipboard.writeText(url).then(
    () => toast.success(t('Link copied')),
    () => toast.error(t('Could not copy the link')),
  );

function FileIcon({ file, className }: { file: Pick<DeskFile, 'mimeType'>; className?: string }) {
  return isImageFile(file.mimeType) ? <ImageIcon className={className} /> : <FileText className={className} />;
}

/** Who made a commit, with what they are: a person, an agent's run, or Deskworks (a Work Item's merge). */
export function CommitAuthor({ author, className }: { author: DeskFileAuthor; className?: string }) {
  const Icon = author.type === 'agent' ? Bot : author.type === 'system' ? GitMerge : User;
  const what = author.type === 'agent' ? t('Agent') : author.type === 'system' ? t('Deskworks (merge)') : t('Person');
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1 align-bottom', className)} title={what}>
      <Icon className="size-3.5 shrink-0" aria-label={what} />
      <span className="truncate">{authorName(author)}</span>
    </span>
  );
}

/** A commit's short id; the whole one in the tooltip. */
export function CommitSha({ sha, className }: { sha: string; className?: string }) {
  return (
    <code className={cn('rounded bg-muted px-1 py-px font-mono text-[11px] text-muted-foreground', className)} title={sha}>
      {shortSha(sha)}
    </code>
  );
}

// ---------------------------------------------------------------- the tree

interface FolderNode {
  path: string;
  name: string;
  folders: FolderNode[];
  files: DeskFile[];
  /** Files in it and below. */
  count: number;
  size: number;
}

/** Folders are what the paths make of them, like git's: there is no empty folder. */
function buildTree(files: DeskFile[]): FolderNode {
  const root: FolderNode = { path: '', name: '', folders: [], files: [], count: 0, size: 0 };
  const folders = new Map<string, FolderNode>([['', root]]);
  const folderOf = (path: string): FolderNode => {
    const known = folders.get(path);
    if (known) return known;
    const node: FolderNode = { path, name: baseName(path), folders: [], files: [], count: 0, size: 0 };
    folderOf(dirName(path)).folders.push(node);
    folders.set(path, node);
    return node;
  };
  for (const file of files) {
    let path: string | null = dirName(file.path);
    folderOf(path).files.push(file);
    // The file counts in its folder and every folder above.
    while (path !== null) {
      const node = folders.get(path)!;
      node.count++;
      node.size += file.size;
      path = path ? dirName(path) : null;
    }
  }
  const sort = (node: FolderNode) => {
    node.folders.sort((a, b) => a.name.localeCompare(b.name));
    node.files.sort((a, b) => baseName(a.path).localeCompare(baseName(b.path)));
    node.folders.forEach(sort);
  };
  sort(root);
  return root;
}

const ancestors = (path: string) => {
  const out: string[] = [];
  for (let p = dirName(path); p; p = dirName(p)) out.push(p);
  return out;
};

const allFolders = (files: DeskFile[]) => new Set(files.flatMap((f) => ancestors(f.path)));

/**
 * The desk's files (docs/features/desk-files.md): every file of main with its version, who changed it and when;
 * a file opens beside the tree with its preview, its versions and what a person can do with it. Changes come live.
 */
export function DeskFiles({ desk }: { desk: DeskDetail }) {
  const navigate = useNavigate();
  const { file: selected } = useSearch({ from: '/_authed/w/$workspaceId/desks/$deskId' });
  const [view, setView] = useState<'files' | 'history'>('files');
  const tree = useQuery({ queryKey: qk.deskFileTree(desk.id), queryFn: () => deskFilesApi.tree(desk.id) });
  const open = useQuery({ queryKey: qk.deskFileWorkItems(desk.id), queryFn: () => deskFilesApi.openWorkItems(desk.id) });
  const select = (path: string | undefined) => {
    setView('files');
    void navigate({ to: '.', search: (s) => ({ ...s, file: path }), replace: true });
  };

  if (tree.isPending) return <LoadingRows rows={6} />;
  if (tree.isError) return <ErrorState error={tree.error} onRetry={() => void tree.refetch()} />;

  return (
    <CanWrite.Provider value={tree.data.canWrite !== false}>
      <div className="flex flex-col gap-6">
        <FilesSummary desk={desk} tree={tree.data} workItems={open.data ?? []} view={view} onView={setView} />
        {view === 'files' ? (
          <FilesBrowser desk={desk} tree={tree.data} selected={selected} onSelect={select} onHistory={() => setView('history')} />
        ) : (
          <Section icon={<History />} title={t('Changes')} description={t('Every change of the desk’s files, newest first: what people saved and each Work Item’s merge.')}>
            <CommitList desk={desk} files={tree.data.files} onOpen={select} />
          </Section>
        )}
      </div>
    </CanWrite.Provider>
  );
}

/** How many files, how large, the last change — and the Work Items whose changes aren't in yet. */
function FilesSummary({
  desk,
  tree,
  workItems,
  view,
  onView,
}: {
  desk: DeskDetail;
  tree: DeskFileTree;
  workItems: DeskOpenWorkItemFiles[];
  view: 'files' | 'history';
  onView: (v: 'files' | 'history') => void;
}) {
  const size = tree.files.reduce((sum, f) => sum + f.size, 0);
  const last = newestChange(tree.files);
  return (
    <Card className="flex flex-col gap-3 px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
          <span>
            <span className="font-semibold tabular-nums">{tree.files.length}</span> {tree.files.length === 1 ? t('file') : t('files')}
          </span>
          <span className="tabular-nums">{formatBytes(size)}</span>
          {last && (
            <span className="min-w-0 text-[13px] text-muted-foreground">
              {tr('Last change {time} by {name}', { time: <RelativeTime value={last.at} />, name: <CommitAuthor author={last.author} /> })}
            </span>
          )}
        </div>
        <div className="flex gap-1" role="group" aria-label={t('Show')}>
          <Button variant={view === 'files' ? 'secondary' : 'ghost'} size="xs" aria-pressed={view === 'files'} onClick={() => onView('files')}>
            <FolderTree /> {t('Files')}
          </Button>
          <Button variant={view === 'history' ? 'secondary' : 'ghost'} size="xs" aria-pressed={view === 'history'} onClick={() => onView('history')}>
            <History /> {t('Changes')}
          </Button>
        </div>
      </div>
      {workItems.length > 0 && (
        <div className="flex flex-col gap-1.5 border-t pt-3 text-[13px]">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <GitBranch className="size-3.5" />
            {workItems.length === 1
              ? t('1 Work Item changed files that aren’t in the desk’s files yet:')
              : t('{n} Work Items changed files that aren’t in the desk’s files yet:', { n: workItems.length })}
          </div>
          <ul className="flex flex-col gap-1">
            {workItems.map((w) => (
              <li key={w.workItemId} className="flex min-w-0 flex-wrap items-center gap-2">
                <Link
                  to="/w/$workspaceId/work-items/$workItemId"
                  params={{ workspaceId: desk.workspaceId, workItemId: w.workItemId }}
                  className="min-w-0 truncate font-medium hover:text-primary hover:underline"
                >
                  {w.title || t('Work Item')}
                </Link>
                <MetaBadge meta={WORK_ITEM_FILES_STATUS[w.status]} />
                <span className="text-xs text-muted-foreground">{w.changedFiles === 1 ? t('1 file') : t('{n} files', { n: w.changedFiles })}</span>
                <RelativeTime value={w.updatedAt} className="text-xs text-muted-foreground" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function FilesBrowser({
  desk,
  tree,
  selected,
  onSelect,
  onHistory,
}: {
  desk: DeskDetail;
  tree: DeskFileTree;
  selected: string | undefined;
  onSelect: (path: string | undefined) => void;
  onHistory: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const canWrite = useContext(CanWrite);
  const phone = useIsPhone();
  const input = useRef<HTMLInputElement>(null);
  const details = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState('');
  const [folder, setFolder] = useState(() => (selected ? dirName(selected) : ''));
  const [open, setOpen] = useState<Set<string>>(() => {
    const start = tree.files.length <= OPEN_ALL_UP_TO ? allFolders(tree.files) : new Set<string>();
    if (selected) ancestors(selected).forEach((p) => start.add(p));
    return start;
  });
  const [uploading, setUploading] = useState(0);
  const [dialog, setDialog] = useState<'new' | 'moveFolder' | null>(null);
  const removeFolder = useApiMutation((path: string) => deskFilesApi.remove(desk.id, path), {
    invalidate: [qk.deskFiles(desk.id)],
    errorTitle: t('Not deleted'),
    success: (_, path) => t('Folder {folder} deleted', { folder: `${path}/` }),
    onSuccess: (_, path) => {
      setFolder('');
      if (selected?.startsWith(`${path}/`)) onSelect(undefined);
    },
  });

  const q = filter.trim().toLowerCase();
  const shown = useMemo(() => (q ? tree.files.filter((f) => f.path.toLowerCase().includes(q)) : tree.files), [tree.files, q]);
  const root = useMemo(() => buildTree(shown), [shown]);
  const file = selected ? tree.files.find((f) => f.path === selected) : undefined;
  // The current folder went away (moved, its last file deleted): back to the top.
  const folderExists = !folder || tree.files.some((f) => f.path.startsWith(`${folder}/`));

  const pick = (path: string) => {
    onSelect(path);
    setFolder(dirName(path));
    setOpen((s) => new Set([...s, ...ancestors(path)]));
    // On a phone the file opens below the tree.
    if (phone) window.setTimeout(() => details.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const toggle = (path: string) => {
    setFolder(path);
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const target = folderExists ? folder : '';
  const add = async (files: FileList) => {
    const list = Array.from(files);
    setUploading((n) => n + list.length);
    let done = 0;
    for (const f of list) {
      const path = joinPath(target, f.name);
      try {
        if (f.size > MAX_UPLOAD_BYTES) throw new Error(t('{name} is larger than 10 MB.', { name: f.name }));
        // A file of that name gets a new version; one that changed meanwhile is a 409.
        const existing = tree.files.find((x) => x.path === path);
        await deskFilesApi.put(desk.id, { path, contentBase64: await fileToBase64(f), baseSha: existing?.sha ?? null });
        done++;
      } catch (err) {
        toast.error(t('{name} was not added', { name: f.name }), errorMessage(err));
      } finally {
        setUploading((n) => n - 1);
      }
    }
    void queryClient.invalidateQueries({ queryKey: qk.deskFiles(desk.id) });
    if (done) toast.success(done === 1 ? t('1 file added') : t('{n} files added', { n: done }));
    if (list.length === 1 && done === 1) pick(joinPath(target, list[0].name));
  };

  const rows: ReactNode[] = [];
  const walk = (node: FolderNode, depth: number) => {
    for (const sub of node.folders) {
      const isOpen = !!q || open.has(sub.path);
      rows.push(
        <li key={`d:${sub.path}`}>
          <button
            type="button"
            onClick={() => toggle(sub.path)}
            aria-expanded={isOpen}
            className={cn('flex w-full items-center gap-2 py-2 pr-5 text-left hover:bg-muted/50', target === sub.path && 'bg-muted/60')}
            style={{ paddingLeft: 16 + depth * 16 }}
          >
            <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-90')} />
            {isOpen ? <FolderOpen className="size-4 shrink-0 text-sky-600 dark:text-sky-400" /> : <Folder className="size-4 shrink-0 text-sky-600 dark:text-sky-400" />}
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{sub.name}</span>
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
              {sub.count === 1 ? t('1 file') : t('{n} files', { n: sub.count })} · {formatBytes(sub.size)}
            </span>
          </button>
        </li>,
      );
      if (isOpen) walk(sub, depth + 1);
    }
    for (const f of node.files) {
      rows.push(
        <li key={`f:${f.path}`} className={cn('flex items-center', f.path === selected && 'bg-primary/5')}>
          <button
            type="button"
            onClick={() => pick(f.path)}
            aria-current={f.path === selected || undefined}
            className="flex min-w-0 flex-1 items-center gap-2 py-2 pr-2 text-left hover:bg-muted/50"
            style={{ paddingLeft: 16 + depth * 16 + 22 }}
          >
            <FileIcon file={f} className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="flex min-w-0 items-center gap-2">
                <span className={cn('truncate text-sm', f.path === selected && 'font-medium text-primary')}>{baseName(f.path)}</span>
                {f.visibility === 'public' && (
                  <Badge tone="green">
                    <Globe /> {t('Public')}
                  </Badge>
                )}
              </span>
              <span className="block truncate text-xs text-muted-foreground" title={f.mimeType}>
                {formatBytes(f.size)} · {fileKind(f.path, f.mimeType)}
                {f.lastChange && (
                  <>
                    {' · '}
                    <CommitAuthor author={f.lastChange.author} /> · <RelativeTime value={f.lastChange.at} />
                  </>
                )}
              </span>
            </span>
          </button>
          {f.publicUrl && (
            <Button variant="ghost" size="icon-sm" className="mr-3" title={t('Copy public link')} aria-label={t('Copy the public link of {name}', { name: baseName(f.path) })} onClick={() => copyLink(f.publicUrl!, toast)}>
              <Copy />
            </Button>
          )}
        </li>,
      );
    }
  };
  walk(root, 0);

  const list =
    tree.files.length === 0 ? (
      <EmptyState
        compact
        title={t('No files yet')}
        description={
          canWrite
            ? t('Upload files, drop them here or create a text file. To make a folder, put a file into it: “knowledge/pricing.md”.')
            : t('The desk’s agents and the people who may change this desk put files here.')
        }
      />
    ) : (
      <>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-40 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('Find a file')} className="h-8 pl-8" aria-label={t('Find a file')} />
          </div>
          {canWrite && target && (
            <span className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md border bg-muted/40 py-0.5 pr-0.5 pl-2 text-xs">
              <FolderOpen className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate" title={t('New and uploaded files go into this folder')}>
                {tr('Adding to {folder}', { folder: <code className="font-mono">{target}/</code> })}
              </span>
              <Button variant="ghost" size="xs" className="h-6 px-1.5" onClick={() => setDialog('moveFolder')} title={t('Rename or move this folder with everything in it')}>
                <Pencil />
              </Button>
              <FolderDelete folder={target} count={tree.files.filter((f) => f.path.startsWith(`${target}/`)).length} onConfirm={() => removeFolder.mutateAsync(target)} />
              <Button variant="ghost" size="xs" className="h-6 px-1.5" onClick={() => setFolder('')} title={t('Add to the top folder')} aria-label={t('Add to the top folder')}>
                <X />
              </Button>
            </span>
          )}
        </div>
        {rows.length === 0 ? (
          <EmptyState compact title={t('No file matches “{q}”', { q: filter.trim() })} />
        ) : (
          <ul className="-mx-5 -mb-5 divide-y border-t">{rows}</ul>
        )}
      </>
    );

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <Section
        className="min-w-0 lg:col-span-3"
        icon={<FolderTree />}
        title={t('Files')}
        description={t('What the desk’s agents read and write while they work. Every change is kept as a version.')}
        actions={
          canWrite && (
            <>
              <input
                ref={input}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) void add(e.target.files);
                  e.target.value = '';
                }}
              />
              <Button variant="outline" size="sm" onClick={() => setDialog('new')}>
                <FilePlus /> {t('New file')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => input.current?.click()} loading={uploading > 0}>
                {uploading === 0 && <Upload />} {t('Upload')}
              </Button>
            </>
          )
        }
      >
        {canWrite ? (
          <DropZone onFiles={(files) => void add(files)} label={target ? t('Drop files to add them to {folder}', { folder: `${target}/` }) : t('Drop files to add them')}>
            {list}
          </DropZone>
        ) : (
          list
        )}
      </Section>

      <div ref={details} className="min-w-0 scroll-mt-4 lg:col-span-2">
        {file ? (
          <FileDetails key={file.path} desk={desk} file={file} onSelect={onSelect} />
        ) : selected ? (
          <Section icon={<FileText />} title={baseName(selected)}>
            <EmptyState
              compact
              title={t('This file isn’t in the desk’s files (any more)')}
              description={t('It may have been moved or deleted. The changes show what happened to it.')}
              action={
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={onHistory}>
                    <History /> {t('Changes')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onSelect(undefined)}>
                    {t('Close')}
                  </Button>
                </div>
              }
            />
          </Section>
        ) : (
          <Section
            icon={<History />}
            title={t('Recent changes')}
            actions={
              <Button variant="ghost" size="xs" onClick={onHistory}>
                {t('View all')}
              </Button>
            }
          >
            <CommitList desk={desk} files={tree.files} onOpen={pick} limit={5} />
          </Section>
        )}
      </div>

      {canWrite && <NewFileDialog desk={desk} folder={target} files={tree.files} open={dialog === 'new'} onOpenChange={(o) => setDialog(o ? 'new' : null)} onCreated={pick} />}
      {canWrite && target && (
        <MoveDialog
          desk={desk}
          from={target}
          folder
          open={dialog === 'moveFolder'}
          onOpenChange={(o) => setDialog(o ? 'moveFolder' : null)}
          onMoved={(to) => {
            setFolder(to);
            setOpen((s) => new Set([...s, to, ...ancestors(`${to}/x`)]));
            if (selected?.startsWith(`${target}/`)) onSelect(`${to}${selected.slice(target.length)}`);
          }}
        />
      )}
    </div>
  );
}

/** Deletes a folder with every file in it, in one commit; the files' versions stay in the changes. */
function FolderDelete({ folder, count, onConfirm }: { folder: string; count: number; onConfirm: () => Promise<unknown> }) {
  return (
    <Confirm
      title={t('Delete the folder {folder}?', { folder: `${folder}/` })}
      description={
        count === 1
          ? t('The 1 file in it is deleted. Its versions stay in the changes, so it can be restored.')
          : t('All {n} files in it are deleted in one change. Their versions stay in the changes, so they can be restored.', { n: count })
      }
      confirmLabel={t('Delete')}
      destructive
      onConfirm={onConfirm}
    >
      <Button variant="ghost" size="xs" className="h-6 px-1.5 hover:text-destructive" title={t('Delete this folder with everything in it')} aria-label={t('Delete this folder with everything in it')}>
        <Trash2 />
      </Button>
    </Confirm>
  );
}

// ---------------------------------------------------------------- one file

function FileDetails({ desk, file, onSelect }: { desk: DeskDetail; file: DeskFile; onSelect: (path: string | undefined) => void }) {
  const toast = useToast();
  const canWrite = useContext(CanWrite);
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const invalidate = [qk.deskFiles(desk.id)];
  const isPublic = file.visibility === 'public';
  const text = isTextFile(file.path, file.mimeType);

  const visibility = useApiMutation(() => deskFilesApi.setVisibility(desk.id, { path: file.path, visibility: isPublic ? 'private' : 'public' }), {
    invalidate,
    errorTitle: t('Could not change who sees it'),
    success: (f) => (f.visibility === 'public' ? t('Public: anyone with the link can open it') : t('Private again: the link no longer works')),
  });
  const remove = useApiMutation(() => deskFilesApi.remove(desk.id, file.path), {
    invalidate,
    errorTitle: t('Not deleted'),
    success: t('File deleted'),
    onSuccess: () => onSelect(undefined),
  });

  const folder = dirName(file.path);
  return (
    <Section
      icon={<FileIcon file={file} />}
      title={<span className="min-w-0 truncate">{baseName(file.path)}</span>}
      description={folder ? <code className="font-mono text-xs break-all">{folder}/</code> : t('In the top folder')}
      actions={
        <Button variant="ghost" size="icon-sm" onClick={() => onSelect(undefined)} title={t('Close')} aria-label={t('Close')}>
          <X />
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {editing ? (
          <TextEditor desk={desk} file={file} onDone={() => setEditing(false)} />
        ) : (
          <FileContent desk={desk} file={file} />
        )}

        {!editing && (
          <div className="flex flex-wrap gap-2">
            {canWrite && text && file.size <= MAX_TEXT_BYTES && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil /> {t('Edit')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              loading={downloading}
              onClick={async () => {
                setDownloading(true);
                try {
                  await downloadDeskFile(desk.id, file.sha, file.path);
                } catch (err) {
                  toast.error(t('Download failed'), errorMessage(err));
                } finally {
                  setDownloading(false);
                }
              }}
            >
              {!downloading && <Download />} {t('Download')}
            </Button>
            {canWrite && (
              <>
                <Button variant="outline" size="sm" loading={visibility.isPending} onClick={() => visibility.mutate()}>
                  {!visibility.isPending && (isPublic ? <Lock /> : <Globe />)} {isPublic ? t('Make private') : t('Make public')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setMoving(true)}>
                  <FolderOpen /> {t('Rename or move')}
                </Button>
                <Confirm
                  title={t('Delete {name}?', { name: baseName(file.path) })}
                  description={t('The desk’s agents no longer see it. Its versions stay in the changes, so it can be restored.')}
                  confirmLabel={t('Delete')}
                  destructive
                  onConfirm={() => remove.mutateAsync()}
                >
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                    <Trash2 /> {t('Delete')}
                  </Button>
                </Confirm>
              </>
            )}
          </div>
        )}

        {isPublic && file.publicUrl && (
          <div className="flex min-w-0 items-center gap-1 rounded-md border bg-muted/30 py-1 pr-1 pl-3">
            <Globe className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <code className="min-w-0 flex-1 truncate text-xs" title={file.publicUrl}>
              {file.publicUrl}
            </code>
            <Button variant="ghost" size="icon-sm" onClick={() => copyLink(file.publicUrl!, toast)} title={t('Copy link')} aria-label={t('Copy link')}>
              <Copy />
            </Button>
            <Button variant="ghost" size="icon-sm" asChild title={tc('verb', 'Open')}>
              <a href={file.publicUrl} target="_blank" rel="noreferrer" aria-label={tc('verb', 'Open')}>
                <ExternalLink />
              </a>
            </Button>
          </div>
        )}

        <DefinitionList
          className="grid-cols-[minmax(90px,auto)_1fr]"
          items={[
            { label: t('Size'), value: formatBytes(file.size) },
            { label: t('Type'), value: <span className="break-all">{file.mimeType}</span> },
            {
              label: t('Changed'),
              value: file.lastChange ? (
                <span className="flex flex-col gap-0.5">
                  <span>{tr('{time} by {name}', { time: <RelativeTime value={file.lastChange.at} />, name: <CommitAuthor author={file.lastChange.author} /> })}</span>
                  <span className="text-xs text-muted-foreground">
                    <CommitSha sha={file.lastChange.sha} /> {file.lastChange.message}
                    {file.lastChange.workItemId && (
                      <>
                        {' · '}
                        <WorkItemLink desk={desk} id={file.lastChange.workItemId} title={file.lastChange.workItemTitle} />
                      </>
                    )}
                  </span>
                </span>
              ) : (
                '—'
              ),
            },
            { label: t('Visible to'), value: isPublic ? t('Anyone with the link') : t('This workspace') },
            { label: t('Content'), value: <code className="font-mono text-xs" title={file.sha}>{shortSha(file.sha)}</code> },
          ]}
        />

        <FileVersions desk={desk} file={file} />
      </div>
      {canWrite && <MoveDialog desk={desk} from={file.path} open={moving} onOpenChange={setMoving} onMoved={(to) => onSelect(to)} />}
    </Section>
  );
}

/** A picture, a PDF's first page or the text; anything else only downloads. */
function FileContent({ desk, file }: { desk: DeskDetail; file: DeskFile }) {
  const [large, setLarge] = useState(false);
  const image = isImageFile(file.mimeType);
  const pdf = isPdfFile(file.mimeType);
  const text = isTextFile(file.path, file.mimeType);

  if (text) {
    if (file.size > MAX_TEXT_BYTES) return <NoPreview text={t('This text is too large to show here. Download it to read it.')} />;
    return <TextPreview desk={desk} file={file} />;
  }
  if (!image && !pdf) return <NoPreview text={t('No preview for this file type. Download it to open it.')} />;
  return (
    <>
      <button
        type="button"
        onClick={() => setLarge(true)}
        className="group relative block w-full cursor-zoom-in rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        title={t('Open {name}', { name: baseName(file.path) })}
      >
        {image ? <ImagePreview desk={desk} file={file} /> : <PdfPreview desk={desk} file={file} />}
        <span className="absolute top-2 right-2 rounded-md bg-background/80 p-1 opacity-0 shadow-xs transition-opacity group-hover:opacity-100">
          <Maximize2 className="size-4" />
        </span>
      </button>
      {large && <LargeView desk={desk} file={file} open onOpenChange={setLarge} />}
    </>
  );
}

function NoPreview({ text }: { text: string }) {
  return <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">{text}</div>;
}

function ImagePreview({ desk, file }: { desk: DeskDetail; file: DeskFile }) {
  const blob = useDeskBlob(desk.id, file.sha, file.mimeType);
  const url = useObjectUrl(blob.data);
  if (blob.isError) return <NoPreview text={errorMessage(blob.error)} />;
  if (!url) return <Skeleton className="h-48 w-full" />;
  return <img src={url} alt={baseName(file.path)} className="max-h-80 w-full rounded-md border bg-muted/30 object-contain" />;
}

function PdfPreview({ desk, file }: { desk: DeskDetail; file: DeskFile }) {
  const blob = useDeskBlob(desk.id, file.sha, file.mimeType);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [pages, setPages] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!blob.data) return;
    let cancelled = false;
    import('@/components/files/pdf-thumb')
      .then((pdf) => (cancelled || !canvas.current ? null : pdf.renderFirstPage(blob.data, canvas.current, 220)))
      .then((n) => !cancelled && n !== null && setPages(n))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [blob.data]);
  if (blob.isError || failed) return <NoPreview text={t('The PDF can’t be shown here. Download it to open it.')} />;
  return (
    <span className="flex flex-col items-start gap-1">
      <canvas ref={canvas} className={cn('rounded-md border bg-white', pages === null && 'h-56 w-[220px] animate-pulse bg-muted/40')} />
      <span className="text-xs text-muted-foreground">PDF{pages ? ` · ${pages === 1 ? t('1 page') : t('{n} pages', { n: pages })}` : ''}</span>
    </span>
  );
}

function LargeView({ desk, file, open, onOpenChange }: { desk: DeskDetail; file: DeskFile; open: boolean; onOpenChange: (open: boolean) => void }) {
  const blob = useDeskBlob(desk.id, file.sha, file.mimeType);
  const url = useObjectUrl(blob.data);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="truncate">{baseName(file.path)}</DialogTitle>
          <DialogDescription>
            {formatBytes(file.size)} · {file.mimeType}
          </DialogDescription>
        </DialogHeader>
        {!url ? (
          <div className="h-[50vh] w-full animate-pulse rounded-md bg-muted/40" />
        ) : isImageFile(file.mimeType) ? (
          <img src={url} alt={baseName(file.path)} className="mx-auto max-h-[72vh] max-w-full rounded-md object-contain" />
        ) : (
          <iframe src={url} title={baseName(file.path)} className="h-[72vh] w-full rounded-md border" />
        )}
      </DialogContent>
    </Dialog>
  );
}

const isMarkdown = (path: string) => /\.(md|markdown)$/i.test(path);

function TextPreview({ desk, file }: { desk: DeskDetail; file: DeskFile }) {
  const content = useDeskBlobText(desk.id, file.sha);
  const [source, setSource] = useState(false);
  if (content.isPending) return <Skeleton className="h-40 w-full" />;
  if (content.isError) return <ErrorState error={content.error} onRetry={() => void content.refetch()} />;
  if (content.data === null) return <NoPreview text={t('This file isn’t plain text. Download it to open it.')} />;
  if (!content.data.trim()) return <NoPreview text={t('This file is empty.')} />;
  const markdown = isMarkdown(file.path);
  return (
    <div className="flex flex-col gap-1">
      {markdown && (
        <div className="flex justify-end">
          <Button variant="ghost" size="xs" onClick={() => setSource((v) => !v)}>
            {source ? t('Show formatted') : t('Show source')}
          </Button>
        </div>
      )}
      {markdown && !source ? (
        <div className="max-h-[28rem] overflow-auto rounded-md border p-4">
          <Markdown text={content.data} />
        </div>
      ) : (
        <pre className="max-h-[28rem] overflow-auto rounded-md border bg-muted/20 p-3 font-mono text-xs leading-5 whitespace-pre-wrap break-words">{content.data}</pre>
      )}
    </div>
  );
}

/**
 * Edits a text file of main. The save names the content the person started from (baseSha); when someone — an agent's
 * merge, another person — changed the file meanwhile, the Control Plane refuses it (409) and the person chooses.
 */
function TextEditor({ desk, file, onDone }: { desk: DeskDetail; file: DeskFile; onDone: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const loaded = useDeskBlobText(desk.id, file.sha);
  const [draft, setDraft] = useState<{ text: string; baseSha: string } | null>(null);
  // After "Load their version": the content to start from again, once the tree has it.
  const [want, setWant] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);

  useEffect(() => {
    if (draft === null && typeof loaded.data === 'string' && (want === null || want === file.sha)) setDraft({ text: loaded.data, baseSha: file.sha });
  }, [draft, loaded.data, file.sha, want]);

  if (loaded.isError) return <ErrorState error={loaded.error} onRetry={() => void loaded.refetch()} />;
  if (loaded.data === null) return <NoPreview text={t('This file isn’t plain text. Download it to open it.')} />;
  if (!draft) return <Skeleton className="h-64 w-full" />;

  // Changed since the editor opened: the tree (live) has another content than the one being edited.
  const changedMeanwhile = conflict || file.sha !== draft.baseSha;
  const save = async (overwrite: boolean) => {
    setSaving(true);
    try {
      const res = await deskFilesApi.put(desk.id, {
        path: file.path,
        contentBase64: textToBase64(draft.text),
        message: message.trim() || undefined,
        ...(overwrite ? {} : { baseSha: draft.baseSha }),
      });
      // The preview shows the new text right away, before the tree comes back with its sha.
      if (res.file) queryClient.setQueryData(qk.deskBlob(desk.id, res.file.sha, 'text'), draft.text);
      void queryClient.invalidateQueries({ queryKey: qk.deskFiles(desk.id) });
      toast.success(res.changed ? t('Saved') : t('Nothing changed'));
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setConflict(true);
      else toast.error(t('Not saved'), errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save(false);
      }}
    >
      <Textarea
        value={draft.text}
        onChange={(e) => setDraft({ ...draft, text: e.target.value })}
        className="min-h-80 font-mono text-xs leading-5 sm:text-xs"
        spellCheck={false}
        aria-label={t('Content of {name}', { name: baseName(file.path) })}
        autoFocus
      />
      <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t('What did you change? (optional)')} aria-label={t('What did you change?')} />
      {changedMeanwhile && (
        <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <p className="font-medium">{t('Someone changed this file while you were editing it.')}</p>
          <p className="mt-0.5">{t('Load their version (your edits are lost — copy them first if you need them), or save yours over it.')}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={async () => {
                await queryClient.refetchQueries({ queryKey: qk.deskFileTree(desk.id) });
                const fresh = queryClient.getQueryData<DeskFileTree>(qk.deskFileTree(desk.id))?.files.find((f) => f.path === file.path);
                setWant(fresh?.sha ?? null);
                setConflict(false);
                setDraft(null);
              }}
            >
              <RotateCcw /> {t('Load their version')}
            </Button>
            <Button type="button" variant="outline" size="xs" loading={saving} onClick={() => void save(true)}>
              {t('Save mine over it')}
            </Button>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          {t('Cancel')}
        </Button>
        <Button type="submit" size="sm" loading={saving} disabled={changedMeanwhile || draft.text === loaded.data}>
          {t('Save')}
        </Button>
      </div>
    </form>
  );
}

/** The file's versions on main (the commits that changed it), newest first; an older one can be put back. */
function FileVersions({ desk, file }: { desk: DeskDetail; file: DeskFile }) {
  const canWrite = useContext(CanWrite);
  const [diff, setDiff] = useState<string | null>(null);
  const history = useQuery({
    queryKey: qk.deskFileHistory(desk.id, file.path),
    queryFn: () => deskFilesApi.history(desk.id, { path: file.path, limit: HISTORY_PAGE }),
  });
  const restore = useApiMutation((commit: string) => deskFilesApi.restore(desk.id, { path: file.path, commit }), {
    invalidate: [qk.deskFiles(desk.id)],
    errorTitle: t('Not restored'),
    success: (r, commit) => (r.changed ? t('The version of {commit} is back', { commit: shortSha(commit) }) : t('That is the current version already')),
  });
  const text = isTextFile(file.path, file.mimeType);

  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <History className="size-3.5" /> {t('Versions')}
      </div>
      {history.isPending ? (
        <LoadingRows rows={2} />
      ) : history.isError ? (
        <ErrorState error={history.error} onRetry={() => void history.refetch()} />
      ) : history.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('No versions yet.')}</p>
      ) : (
        <ol className="divide-y rounded-md border">
          {history.data.map((c, k) => {
            const kind = c.changes.find((x) => x.path === file.path)?.status ?? 'changed';
            const current = file.lastChange ? c.sha === file.lastChange.sha : k === 0;
            // The version before is the next older commit of this path (when it is in the list).
            const older = history.data[k + 1];
            return (
              <li key={c.sha} className="px-3 py-2">
                <div className="flex items-start gap-2">
                  <CommitSha sha={c.sha} className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm break-words">{c.message || CHANGE_KIND[kind].label}</div>
                    <div className="text-xs text-muted-foreground">
                      <Badge tone={CHANGE_KIND[kind].tone} className="mr-1 px-1.5 py-0 text-[11px]">
                        {CHANGE_KIND[kind].label}
                      </Badge>
                      <CommitAuthor author={c.author} /> · <RelativeTime value={c.at} />
                      {c.workItemId && (
                        <>
                          {' · '}
                          <WorkItemLink desk={desk} id={c.workItemId} title={c.workItemTitle} />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {text && kind === 'changed' && older && (
                      <Button variant="ghost" size="xs" aria-expanded={diff === c.sha} onClick={() => setDiff((d) => (d === c.sha ? null : c.sha))}>
                        {diff === c.sha ? t('Hide') : t('What changed')}
                      </Button>
                    )}
                    {current ? (
                      <Badge tone="green">{t('Current')}</Badge>
                    ) : (
                      canWrite &&
                      kind !== 'deleted' && (
                        <Button
                          variant="outline"
                          size="xs"
                          loading={restore.isPending && restore.variables === c.sha}
                          disabled={restore.isPending}
                          onClick={() => restore.mutate(c.sha)}
                          title={t('Put this version back as the current one (a new change; nothing is lost)')}
                        >
                          {!(restore.isPending && restore.variables === c.sha) && <RotateCcw />} {t('Restore')}
                        </Button>
                      )
                    )}
                  </div>
                </div>
                {diff === c.sha && older && (
                  <TextDiff deskId={desk.id} before={{ commit: older.sha, path: file.path }} after={{ commit: c.sha, path: file.path }} className="mt-2" />
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function WorkItemLink({ desk, id, title }: { desk: DeskDetail; id: string; title: string | null }) {
  return tr('from {item}', {
    item: (
      <Link to="/w/$workspaceId/work-items/$workItemId" params={{ workspaceId: desk.workspaceId, workItemId: id }} className="font-medium text-foreground hover:text-primary hover:underline">
        {title ?? t('a Work Item')}
      </Link>
    ),
  });
}

// ---------------------------------------------------------------- changes of main

/** main's commits, newest first: who, when, which Work Item, and what happened to each path. */
function CommitList({ desk, files, onOpen, limit }: { desk: DeskDetail; files: DeskFile[]; onOpen: (path: string) => void; limit?: number }) {
  const history = useInfiniteQuery({
    queryKey: [...qk.deskFileHistory(desk.id), limit ?? 'all'],
    queryFn: ({ pageParam }) => deskFilesApi.history(desk.id, { skip: pageParam || undefined, limit: limit ?? HISTORY_PAGE }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (!limit && last.length === HISTORY_PAGE ? all.reduce((n, page) => n + page.length, 0) : undefined),
  });
  const restore = useApiMutation((v: { path: string; commit: string }) => deskFilesApi.restore(desk.id, { ...v, before: true }), {
    invalidate: [qk.deskFiles(desk.id)],
    errorTitle: t('Not restored'),
    success: (_, v) => t('{path} is back', { path: v.path }),
  });
  const paths = useMemo(() => new Set(files.map((f) => f.path)), [files]);

  if (history.isPending) return <LoadingRows rows={3} />;
  if (history.isError) return <ErrorState error={history.error} onRetry={() => void history.refetch()} />;
  const commits = history.data.pages.flat();
  if (!commits.length) return <EmptyState compact title={t('No changes yet')} description={t('Every upload, edit and every Work Item’s merged changes show up here.')} />;

  return (
    <>
      <ol className="-mx-5 -mb-5 divide-y border-t">
        {commits.map((c) => (
          <CommitRow key={c.sha} desk={desk} commit={c} paths={paths} onOpen={onOpen} onRestore={(path) => restore.mutate({ path, commit: c.sha })} compact={!!limit} />
        ))}
      </ol>
      {history.hasNextPage && (
        <div className="-mx-5 mt-5 -mb-5 border-t px-5 py-2">
          <Button variant="ghost" size="xs" loading={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
            {t('Show older changes')}
          </Button>
        </div>
      )}
    </>
  );
}

const CHANGES_SHOWN = 12;

function CommitRow({
  desk,
  commit: c,
  paths,
  onOpen,
  onRestore,
  compact,
}: {
  desk: DeskDetail;
  commit: DeskFileCommit;
  paths: Set<string>;
  onOpen: (path: string) => void;
  /** Puts back a path this commit deleted. */
  onRestore: (path: string) => void;
  compact: boolean;
}) {
  const canWrite = useContext(CanWrite);
  const [all, setAll] = useState(false);
  const shown = all ? c.changes : c.changes.slice(0, compact ? 3 : CHANGES_SHOWN);
  return (
    <li className="px-5 py-3">
      <div className="flex min-w-0 items-start gap-2">
        <CommitSha sha={c.sha} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium break-words">{c.message || (c.changes.length === 1 ? t('1 file changed') : t('{n} files changed', { n: c.changes.length }))}</div>
          <div className="text-xs text-muted-foreground">
            <CommitAuthor author={c.author} /> · <RelativeTime value={c.at} />
            {c.workItemId && (
              <>
                {' · '}
                <WorkItemLink desk={desk} id={c.workItemId} title={c.workItemTitle} />
              </>
            )}
          </div>
        </div>
      </div>
      <ul className="mt-1.5 flex flex-col gap-1">
        {shown.map((x) => {
          const there = paths.has(x.path);
          return (
            <li key={x.path} className="flex min-w-0 items-center gap-2 text-xs">
              <Badge tone={CHANGE_KIND[x.status].tone} className="w-[4.75rem] justify-center px-1.5 py-0 text-[11px]">
                {CHANGE_KIND[x.status].label}
              </Badge>
              {there ? (
                <button type="button" onClick={() => onOpen(x.path)} className="min-w-0 truncate text-left font-mono hover:text-primary hover:underline" title={x.path}>
                  {x.path}
                </button>
              ) : (
                <span className="min-w-0 truncate font-mono text-muted-foreground" title={x.path}>
                  {x.path}
                </span>
              )}
              {canWrite && x.status === 'deleted' && !there && !compact && (
                <Button variant="ghost" size="xs" className="ml-auto h-6 shrink-0" onClick={() => onRestore(x.path)} title={t('Put the file back as it was before it was deleted')}>
                  <RotateCcw /> {t('Restore')}
                </Button>
              )}
            </li>
          );
        })}
        {shown.length < c.changes.length && (
          <li>
            <button type="button" onClick={() => setAll(true)} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
              {t('and {n} more', { n: c.changes.length - shown.length })}
            </button>
          </li>
        )}
      </ul>
    </li>
  );
}

// ---------------------------------------------------------------- dialogs

/** A new text file; its folder comes with the path ("knowledge/pricing.md" makes the folder knowledge). */
function NewFileDialog({
  desk,
  folder,
  files,
  open,
  onOpenChange,
  onCreated,
}: {
  desk: DeskDetail;
  folder: string;
  files: DeskFile[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (path: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [path, setPath] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPath(folder ? `${folder}/` : '');
    setContent('');
    setError(null);
  }, [open, folder]);

  const clean = cleanPath(path);
  // "knowledge/" is a folder without a file in it yet.
  const named = !!clean && !path.trim().endsWith('/');
  const taken = files.some((f) => f.path === clean);
  const submit = async () => {
    if (!named || taken) return;
    setSaving(true);
    setError(null);
    try {
      await deskFilesApi.put(desk.id, { path: clean, contentBase64: textToBase64(content), baseSha: null, message: `Created ${clean}` });
      void queryClient.invalidateQueries({ queryKey: qk.deskFiles(desk.id) });
      toast.success(t('{name} created', { name: baseName(clean) }));
      onOpenChange(false);
      onCreated(clean);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? t('There is a file {path} already. Choose another name.', { path: clean }) : errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('New text file')}</DialogTitle>
            <DialogDescription>{t('A slash makes a folder: “knowledge/pricing.md” puts pricing.md into the folder knowledge.')}</DialogDescription>
          </DialogHeader>
          <Field label={t('Name')} error={taken ? t('There is a file {path} already. Choose another name.', { path: clean }) : undefined}>
            <Input value={path} onChange={(e) => setPath(e.target.value)} placeholder="knowledge/notes.md" autoFocus spellCheck={false} />
          </Field>
          <Field label={t('Content')} optional>
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} className="min-h-64 font-mono text-xs leading-5 sm:text-xs" spellCheck={false} />
          </Field>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!named || taken}>
              {t('Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Renames or moves a file — or a folder with everything in it — in one change. */
function MoveDialog({
  desk,
  from,
  folder,
  open,
  onOpenChange,
  onMoved,
}: {
  desk: DeskDetail;
  from: string;
  folder?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMoved: (to: string) => void;
}) {
  const [to, setTo] = useState(from);
  useEffect(() => {
    if (open) setTo(from);
  }, [open, from]);
  const move = useApiMutation((target: string) => deskFilesApi.move(desk.id, { from, to: target }), {
    invalidate: [qk.deskFiles(desk.id)],
    errorTitle: t('Not moved'),
    success: (_, target) => t('Moved to {path}', { path: target }),
    onSuccess: (_, target) => {
      onOpenChange(false);
      onMoved(target);
    },
  });
  const target = cleanPath(to);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (target && target !== from) move.mutate(target);
          }}
        >
          <DialogHeader>
            <DialogTitle>{folder ? t('Rename or move folder') : t('Rename or move')}</DialogTitle>
            <DialogDescription>
              {folder
                ? t('Everything in {path}/ moves along. Public files stay public, at their new address.', { path: from })
                : t('Change the name, or the folders before it to move the file. A public file stays public, at its new address.')}
            </DialogDescription>
          </DialogHeader>
          <Field label={folder ? t('Folder') : t('Path')}>
            <Input value={to} onChange={(e) => setTo(e.target.value)} autoFocus spellCheck={false} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={move.isPending} disabled={!target || target === from}>
              {t('Move')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
